import React, { useState, useCallback, useMemo } from 'react';
import {
  Col,
  Row,
  Spinner,
  Pagination,
  Form,
  InputGroup,
  Button,
  Container
} from 'react-bootstrap';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import Section from 'components/base/Section';
import BrandItem from 'components/common/BrandItem';
import {
  getBrandsByPagination,
  BrandPagination
} from 'api/categories/_requests';

const ITEMS_PER_PAGE = 50;

const BrandsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () =>
      debounce((val: string) => {
        setPage(1);
        setSearch(val);
      }, 400),
    []
  );

  const { data, isLoading, isError, isFetching } = useQuery<BrandPagination>({
    queryKey: ['brands', page, search, ITEMS_PER_PAGE],
    queryFn: () => getBrandsByPagination(page, ITEMS_PER_PAGE, search),
    placeholderData: keepPreviousData,
    staleTime: 60_000
  });

  const totalPages = data?.totalPages ?? 1;

  const handlePageChange = useCallback(
    (p: number) => {
      if (p >= 1 && p <= totalPages) setPage(p);
    },
    [totalPages]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    debouncedSetSearch(val);
  };

  const clearSearch = () => {
    setInput('');
    debouncedSetSearch('');
  };

  const renderPagination = () => {
    const windowSize = 2;
    const pageNumbers = Array.from(
      { length: totalPages },
      (_, i) => i + 1
    ).filter(p => Math.abs(p - page) <= windowSize);

    return (
      <Container className="px-xl-0 px-xxl-3">
        <Pagination className="justify-content-center mt-4 flex-wrap">
          <Pagination.First
            disabled={page === 1}
            onClick={() => handlePageChange(1)}
          />
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          />
          {pageNumbers[0] > 1 && <Pagination.Ellipsis disabled />}
          {pageNumbers.map(p => (
            <Pagination.Item
              key={p}
              active={p === page}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </Pagination.Item>
          ))}
          {pageNumbers.at(-1)! < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          />
          <Pagination.Last
            disabled={page === totalPages}
            onClick={() => handlePageChange(totalPages)}
          />
        </Pagination>
      </Container>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center pt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-danger pt-5">
        Failed to load brands. Please try again.
      </div>
    );
  }

  return (
    <div className="pt-5 mb-9">
      <Container className="px-xl-0 px-xxl-3">
        <header className="mb-5 d-flex flex-wrap justify-content-between align-items-end gap-3">
          <div>
            <h2 className="fw-bold mb-1">Brands</h2>
            <p className="text-body-secondary mb-0">
              {search
                ? `Results for “${search}”`
                : 'Shop from your favourite brands'}
            </p>
          </div>
          <InputGroup style={{ maxWidth: 280 }}>
            <Form.Control
              type="text"
              placeholder="Search brands…"
              value={input}
              onChange={handleSearchChange}
            />
            {input && (
              <Button variant="outline-secondary" onClick={clearSearch}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
          </InputGroup>
        </header>

        <Row className="gx-3 gy-4">
          {data?.docs.map(brand => (
            <Col key={brand._id} xs={6} sm={4} md={3} lg={2}>
              <BrandItem brand={brand} />
            </Col>
          ))}
        </Row>

        {renderPagination()}

        {isFetching && (
          <div className="text-center py-2">
            <Spinner size="sm" animation="border" />
          </div>
        )}
      </Container>
    </div>
  );
};

export default React.memo(BrandsPage);
